import { post, get, put } from './api';
import { API_ENDPOINTS, API_CONFIG } from './config';

// 初始化上传响应
export interface InitUploadResponse {
  fileId: string;
  tosKey: string;
  uploadUrl: string;
}

// 上传结果（包含时长或文本长度信息）
export interface UploadResult {
  fileId: string;
  durationSeconds?: number;
  textLength?: number;
}

// 初始化文件上传 - 获取预签名URL
export async function initUpload(data: {
  fileName: string;
  fileSizeBytes: number;
  fileFormat: string;
  contentType?: string;
}): Promise<InitUploadResponse> {
  const response = await post<InitUploadResponse>(API_ENDPOINTS.FILES.INIT_UPLOAD, data);
  return response.data;
}

// 上传文件到TOS（使用预签名URL直传）
export async function uploadToTOS(uploadUrl: string, file: File) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!response.ok) throw new Error('文件上传失败');
  return true;
}

// 确认上传完成响应
export interface ConfirmUploadResponse {
  fileId: string;
  textLength?: number;
  durationSeconds?: number;
}

// 确认上传完成
export async function confirmUpload(fileId: string, tosKey: string): Promise<ConfirmUploadResponse> {
  const response = await post<ConfirmUploadResponse>(API_ENDPOINTS.FILES.COMPLETE_UPLOAD, { fileId, tosKey });
  return response.data;
}

// 更新文件时长
export async function updateFileDuration(fileId: string, durationSeconds: number) {
  const response = await put(`/files/${fileId}/duration`, { durationSeconds });
  return response.data;
}

// 获取音频/视频文件时长（浏览器端解析）
export function getMediaDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const media = file.type.startsWith('video/')
      ? document.createElement('video')
      : document.createElement('audio');

    media.preload = 'metadata';

    media.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const durationSeconds = Math.round(media.duration);
      resolve(durationSeconds);
    };

    media.onerror = () => {
      URL.revokeObjectURL(url);
      // 解析失败时返回 0，不阻塞上传流程
      resolve(0);
    };

    media.src = url;
  });
}

// 判断是否为音频/视频文件
export function isMediaFile(file: File): boolean {
  const mediaTypes = ['audio/', 'video/'];
  const mediaExtensions = /\.(mp3|wav|m4a|ogg|flac|mp4)$/i;
  return mediaTypes.some(type => file.type.startsWith(type)) || mediaExtensions.test(file.name);
}

// 音频时长上限（秒）：4 小时
const MAX_AUDIO_DURATION_SECONDS = 4 * 60 * 60;

// 完整上传流程
export async function uploadFile(file: File): Promise<UploadResult> {
  // 1. 媒体文件先解析时长并校验上限（在上传前拦截，避免浪费带宽）
  let durationSeconds = 0;
  if (isMediaFile(file)) {
    try {
      durationSeconds = await getMediaDuration(file);
      if (durationSeconds > MAX_AUDIO_DURATION_SECONDS) {
        const maxHours = MAX_AUDIO_DURATION_SECONDS / 3600;
        throw new Error(`音频时长超过${maxHours}小时上限，请裁剪后重试 / Audio exceeds ${maxHours}-hour limit, please trim and retry`);
      }
    } catch (e: any) {
      if (e.message?.includes('上限') || e.message?.includes('limit')) throw e;
      console.warn('Failed to get media duration:', e);
    }
  }

  // 2. 初始化上传
  const initRes = await initUpload({
    fileName: file.name,
    fileSizeBytes: file.size,
    fileFormat: file.name.split('.').pop() || '',
    contentType: file.type,
  });

  // 3. 上传到TOS
  await uploadToTOS(initRes.uploadUrl, file);

  // 4. 确认上传（需要传递 tosKey）
  const confirmRes = await confirmUpload(initRes.fileId, initRes.tosKey);

  // 5. 回写时长到后端
  if (isMediaFile(file)) {
    if (durationSeconds > 0) {
      try {
        await updateFileDuration(initRes.fileId, durationSeconds);
      } catch (e) {
        console.warn('Failed to update file duration:', e);
      }
    }
  } else if (confirmRes?.durationSeconds) {
    durationSeconds = confirmRes.durationSeconds;
  }

  return {
    fileId: initRes.fileId,
    durationSeconds,
    textLength: confirmRes?.textLength,
  };
}

// 获取文件下载URL（用于音频播放）
export interface DownloadUrlResponse {
  fileName: string;
  downloadUrl: string;
}

export async function getFileDownloadUrl(fileId: string, expires?: number): Promise<DownloadUrlResponse> {
  const params = expires ? `?expires=${expires}` : '';
  const response = await get<DownloadUrlResponse>(`/files/${fileId}/download-url${params}`);
  return response.data;
}
